import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { selectTenant } from '../store/slices/authSlice'
import './landing/landing.css'
import './landing/demo.css'

/* ================================================================
   CUARTEL DIGITAL — Landing v3 (alta fidelidad)
   Port exacto del prototipo design_handoff_cuartel_digital/
   "Cuartel Digital - Landing v2.html"
   ================================================================ */

/* ---- helpers ---- */
const pad = n => String(n).padStart(2, '0')
const getNow = () => {
  const d = new Date()
  return pad(d.getHours()) + ':' + pad(d.getMinutes()) + ':' + pad(d.getSeconds())
}
const getHM = () => {
  const d = new Date()
  return pad(d.getHours()) + ':' + pad(d.getMinutes())
}

/* ---- Reveal / IntersectionObserver ---- */
function useReveal(threshold = 0.16) {
  const ref = useRef(null)
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (prefersReduced) { setVisible(true); return }
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); io.disconnect() }
    }, { threshold })
    io.observe(el)
    // fallback
    const t = setTimeout(() => setVisible(true), 1600)
    return () => { io.disconnect(); clearTimeout(t) }
  }, [threshold])
  return [ref, visible]
}

/* ---- RevealDiv: wraps children in a .rev div ---- */
function Rev({ children, className = '', style = {} }) {
  const [ref, visible] = useReveal()
  return (
    <div
      ref={ref}
      className={`rev${className ? ' ' + className : ''}`}
      style={visible ? { transform: 'none', ...style } : style}
    >
      {children}
    </div>
  )
}

/* ---- Brand SVG mark ---- */
const BrandMark = () => (
  <svg width="22" height="22" viewBox="0 0 64 64" fill="none" stroke="#fff" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 40a20 20 0 0 1 40 0Z" fill="rgba(255,255,255,0.14)" />
    <path d="M8 40h48v3a5 5 0 0 1-5 5H13a5 5 0 0 1-5-5Z" fill="rgba(255,255,255,0.2)" />
    <path d="M28 26h8l-1.5 9h-5Z" fill="#fff" stroke="none" />
  </svg>
)

/* ---- Check icon for module features ---- */
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6 9 17l-5-5" />
  </svg>
)

/* ---- Arrow right icon for CTAs ---- */
const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12" />
    <path d="M13 6l6 6-6 6" />
  </svg>
)

/* ================================================================
   MÓDULOS DATA
   ================================================================ */
const MODULES = [
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" />
      </svg>
    ),
    tag: 'Activo', tagClass: 'live',
    title: 'Dotación',
    desc: 'Fichas completas de bomberos: cargos, rangos, tipos, antigüedad y estados (activo, suspendido, baja, renuncia).',
    feats: ['Fichas completas', 'Estados con historial', 'Cargos y rangos'],
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
      </svg>
    ),
    tag: 'Activo', tagClass: 'live',
    title: 'Guardia Nocturna',
    desc: 'Planilla mensual, asignación de noches, cobertura, intercambios y ranking de cumplimiento actualizado.',
    feats: ['Planilla mensual', 'Ranking de cumplimiento', 'Intercambios y libro'],
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8ZM14 2v6h6" />
      </svg>
    ),
    tag: 'Activo', tagClass: 'live',
    title: 'Citaciones',
    desc: 'Convocatorias, confirmaciones y control de asistencia por bombero y compañía.',
    feats: ['Convocatorias', 'Control de asistencia', 'Avisos a convocados'],
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10 17h4V5H2v12h3M14 9h4l3 3v5h-3" />
      </svg>
    ),
    tag: 'Activo', tagClass: 'live',
    title: 'Material Mayor',
    desc: 'Flota de carros con cajoneras, patentes, mantenciones programadas y alertas.',
    feats: ['Carros y patentes', 'Cajoneras', 'Mantenciones e historial'],
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2 4 5v6c0 5 3.5 9 8 11 4.5-2 8-6 8-11V5l-8-3Z" />
      </svg>
    ),
    tag: 'Activo', tagClass: 'live',
    title: 'Material Menor',
    desc: 'Equipo personal: asignaciones, movimientos, trazabilidad y alertas de stock.',
    feats: ['Asignación por bombero', 'Movimientos auditados', 'Alertas de stock'],
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 7h20v10H2zM12 12h.01" />
      </svg>
    ),
    tag: 'Activo', tagClass: 'live',
    title: 'Cuotas y Tesorería',
    desc: 'Recaudación, morosos, exenciones y reportes listos para tesorería.',
    feats: ['Estados de pago', 'Morosos y cobranza', 'Reportes y estadísticas'],
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2M9 14l2 2 4-4" />
      </svg>
    ),
    tag: 'Activo', tagClass: 'live',
    title: 'Asistencias',
    desc: 'Cuadro de listas y registro de asistencia a actos del servicio y actividades de la compañía.',
    feats: ['Cuadro de listas', 'Registro por acto', 'Estadísticas de asistencia'],
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0" />
      </svg>
    ),
    tag: 'Activo', tagClass: 'live',
    title: 'Notificaciones y Correos',
    desc: 'Avisos dentro de la aplicación y correos automáticos para asignaciones de guardia, citaciones y licencias.',
    feats: ['Avisos dentro de la app', 'Plantillas por compañía', 'Preferencias por usuario'],
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 15a6 6 0 1 0 0-12 6 6 0 0 0 0 12ZM8.2 13.9 7 22l5-3 5 3-1.2-8.1" />
      </svg>
    ),
    tag: 'Activo', tagClass: 'live',
    title: 'Especialidades',
    desc: 'Certificaciones y especialidades por bombero, con seguimiento de cada voluntario.',
    feats: ['Certificaciones', 'Bomberos por especialidad', 'Seguimiento'],
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 21h18M6 18v-6M12 18V8M18 18v-3" />
      </svg>
    ),
    tag: 'Activo', tagClass: 'live',
    title: 'Estadísticas',
    desc: 'Disponibilidad diaria y mensual, licencias y KPIs para la Capitanía.',
    feats: ['Disponibilidad', 'Tendencias mensuales', 'KPIs por compañía'],
  },
]

/* ================================================================
   LANDING PAGE
   ================================================================ */
/* ---- Demo real: la app completa embebida (tenant demo de solo lectura) ----
   Iframe full-bleed a /demo/dashboard. Carga lazy: el iframe recién se monta
   cuando la sección se acerca al viewport (no penaliza el load de la landing). */
function DemoRealApp() {
  const hostRef = useRef(null)
  const [cargar, setCargar] = useState(false)

  useEffect(() => {
    const el = hostRef.current
    if (!el) return
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setCargar(true)
          io.disconnect()
        }
      },
      { rootMargin: '600px' }
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])

  return (
    <div
      ref={hostRef}
      style={{
        width: '100vw',
        marginLeft: 'calc(50% - 50vw)',
        height: '88vh',
        minHeight: 560,
        marginTop: 28,
        marginBottom: 8,
        background: '#090c13',
        position: 'relative',
      }}
    >
      {cargar ? (
        <iframe
          src="/demo/dashboard"
          title="Demo en vivo de Cuartel Digital — datos de ejemplo, los cambios no se guardan"
          style={{ width: '100%', height: '100%', border: 0, display: 'block' }}
          loading="lazy"
        />
      ) : (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#6f7a90',
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: 12,
            letterSpacing: '0.2em',
          }}
        >
          CARGANDO DEMO EN VIVO…
        </div>
      )}
    </div>
  )
}

export default function LandingPage() {
  const navigate = useNavigate()
  // La landing es visible con o sin sesión (escribir "/" ya no rebota al dashboard), así que
  // el nav ofrece el camino de vuelta al panel en vez de invitar a iniciar sesión de nuevo.
  const { isAuthenticated } = useSelector((state) => state.auth)
  const tenant = useSelector(selectTenant)
  const conSesion = isAuthenticated && !!tenant?.slug
  const [clock, setClock] = useState(getNow())
  const [hm, setHm] = useState(getHM())

  // live clock
  useEffect(() => {
    document.title = 'Cuartel Digital — Gestión Administrativa Integral para Bomberos de Chile'
    const t = setInterval(() => {
      setClock(getNow())
      setHm(getHM())
    }, 1000)
    return () => clearInterval(t)
  }, [])

  const scrollTo = (id) => {
    const el = document.getElementById(id)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <div className="cd-landing">

      {/* ===================== NAV ===================== */}
      <nav className="nav">
        <div className="wrap nav-in">
          <div className="brand">
            <div className="brand-mark"><BrandMark /></div>
            <div>
              <div className="brand-name">CUARTEL <span>DIGITAL</span></div>
              <div className="brand-tag">CL · Bomberos de Chile</div>
            </div>
          </div>
          <div className="nav-links">
            <a onClick={() => scrollTo('inicio')}>Inicio</a>
            <a onClick={() => scrollTo('modulos')}>Módulos</a>
            <a onClick={() => scrollTo('demo')}>Demo</a>
            <a onClick={() => scrollTo('operaciones')}>Operaciones</a>
            <a onClick={() => scrollTo('sumate')}>Contacto</a>
          </div>
          <div className="nav-actions">
            {conSesion
              ? <Link className="btn btn-ghost" to={`/${tenant.slug}/dashboard`}>Ir a mi panel</Link>
              : <Link className="btn btn-ghost" to="/sign-in">Iniciar sesión</Link>}
            <button className="btn btn-primary" onClick={() => scrollTo('sumate')}>
              Registrar Compañía
              <ArrowIcon />
            </button>
          </div>
        </div>
      </nav>

      {/* ===================== HERO ===================== */}
      <header className="hero" id="inicio">
        <div className="hero-bg" />
        <div className="hero-grid-lines" />
        <div className="wrap">
          <div className="hero-in">
            <div>
              <span className="pill"><span className="dot dot-red pulse" />Plataforma web · Bomberos de Chile</span>
              <div className="eyebrow">Gestión digital · <span>{clock}</span> CLT</div>
              <h1 className="hero-title">
                Tu cuartel,<br />
                <span className="accent">bajo control.</span>
                <span className="light">En una sola pantalla.</span>
              </h1>
              <p className="hero-sub">Una plataforma gratuita creada para apoyar la gestión de compañías de Bomberos. Dotación, Guardia Nocturna, Citaciones, Material Mayor y Menor, Cuotas, Asistencias, avisos dentro de la aplicación y correos en eventos habilitados — en un solo Cuartel Digital.</p>
              <div className="hero-ctas">
                <button className="btn btn-primary btn-lg" onClick={() => scrollTo('sumate')}>
                  Registrar mi compañía
                  <ArrowIcon />
                </button>
                <button className="btn btn-ghost btn-lg" onClick={() => scrollTo('demo')}>Ver la plataforma</button>
              </div>
              <div className="hero-trust">
                <span><strong>Gratuito</strong></span>
                <span className="sep" />
                <span>Registro <strong>en línea</strong></span>
                <span className="sep" />
                <span>Acceso <strong>por compañía</strong></span>
              </div>
            </div>
          </div>
        </div>
        <div className="tape" />
      </header>

      <div className="tape" />

      {/* ===================== DEMO INTERACTIVA ===================== */}
      <section className="section demo-sec" id="demo">
        <div className="wrap">
          <div className="sec-head rev in">
            <div>
              <div className="sec-eyebrow">Demo en vivo · Sin registro</div>
              <h2 className="sec-title">
                <span className="hl">PRUÉBALA <em>AHORA MISMO.</em></span>
                <span className="hl">SIN CREAR CUENTA.</span>
              </h2>
            </div>
            <p className="sec-desc">Navega los módulos como si fueras parte de la compañía. Toca cada pestaña para ver de qué se encarga — esto es solo una muestra de lo que vive dentro del cuartel digital.</p>
          </div>

        </div>

        {/* La app REAL, full-bleed (no es una maqueta): tenant demo de solo
            lectura con sesión anónima; los cambios del visitante viven solo en
            su pestaña. Carga lazy al acercarse al viewport. */}
        <DemoRealApp />

        <div className="wrap">
          <div className="demo-cta rev in">
            <p className="hint">↑ Esta es la aplicación real con datos de ejemplo · navega los módulos con el menú lateral · nada de lo que hagas se guarda</p>
            <button className="btn btn-primary btn-lg" style={{marginTop: '14px'}} onClick={() => scrollTo('sumate')}>
              Quiero esto en mi compañía
              <ArrowIcon />
            </button>
          </div>
        </div>
      </section>

      {/* ===================== MÓDULOS ===================== */}
      <section className="section" id="modulos">
        <div className="wrap">
          <Rev>
            <div className="sec-head">
              <div>
                <div className="sec-eyebrow">Sistema integral · 10 módulos</div>
                <h2 className="sec-title">
                  <span className="hl">TODO EL <em>CUARTEL</em>,</span>
                  <span className="hl">DIGITAL Y EN UN</span>
                  <span className="hl">SOLO LUGAR.</span>
                </h2>
              </div>
              <p className="sec-desc">Diez módulos diseñados desde adentro del cuartel, con los flujos reales de los bomberos voluntarios chilenos: Capitanes, Tenientes, Maquinistas, Tesoreros y todo el voluntariado.</p>
            </div>
          </Rev>
          <div className="mods">
            {MODULES.map((m, i) => (
              <Rev key={i}>
                <div className="mod">
                  <div className="mod-head">
                    <div className="mod-ico">{m.icon}</div>
                    <div className={`mod-tag ${m.tagClass}`}>{m.tag}</div>
                  </div>
                  <h3 className="mod-title">{m.title}</h3>
                  <p className="mod-desc">{m.desc}</p>
                  <div className="mod-feats">
                    {m.feats.map((f, j) => (
                      <div className="mod-feat" key={j}>
                        <CheckIcon />
                        <span>{f}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </Rev>
            ))}
          </div>
        </div>
      </section>

      <div className="tape" />

      {/* ===================== OPERACIONES ===================== */}
      <section className="section ops" id="operaciones">
        <div className="wrap">
          <div className="ops-grid">
            {/* screen mock */}
            <Rev>
              <div className="screen">
                <div className="screen-bar">
                  <div className="dots"><span /><span /><span /></div>
                  <div className="screen-bar-mid">cuarteldigital.cl/guardia-nocturna</div>
                  <div className="screen-bar-mid">{hm}</div>
                </div>
                <div className="screen-body">
                  <div className="screen-title">
                    <h4>Ranking · Mayo</h4>
                    <span className="pill"><span className="dot dot-cyan pulse" />Actualizado</span>
                  </div>
                  <div className="tlist">
                    {[
                      { rank:'01', bg:'#c11a24', initials:'JS', name:'Juan Pablo Soto',   meta:'Capitán · 2ª Cía',    pct:'96' },
                      { rank:'02', bg:'#1f6f8b', initials:'MF', name:'Matías Fuentes',    meta:'Teniente 1° · 2ª Cía',pct:'94' },
                      { rank:'03', bg:'#9a6a1f', initials:'CV', name:'Camila Valdés',     meta:'Ayudante · 2ª Cía',   pct:'92' },
                      { rank:'04', bg:'#3a6b3a', initials:'RS', name:'Rodrigo Sepúlveda', meta:'Maquinista · 2ª Cía',  pct:'90' },
                      { rank:'05', bg:'#5b4b8a', initials:'JI', name:'Javiera Iturra',    meta:'Voluntaria · 2ª Cía',  pct:'88' },
                      { rank:'06', bg:'#1f5f6f', initials:'FR', name:'Francisca Reyes',   meta:'Secretaria · 2ª Cía',  pct:'83' },
                    ].map(r => (
                      <div className="titem" key={r.rank}>
                        <div className="rank disp">{r.rank}</div>
                        <div className="av" style={{background: r.bg}}>{r.initials}</div>
                        <div>
                          <div className="name">{r.name}</div>
                          <div className="meta">{r.meta}</div>
                        </div>
                        <div className="pct disp">{r.pct}<span className="u">%</span></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Rev>

            {/* ops text side */}
            <Rev className="ops-side">
              <div className="sec-eyebrow">Operación conectada</div>
              <h3>
                <span className="hl">DEL CUARTEL</span>
                <span className="hl"><em>AL CELULAR.</em></span>
                <span className="hl">DESDE <em>LA WEB.</em></span>
              </h3>
              <p>Los datos guardados quedan disponibles para las personas autorizadas de la compañía desde cualquier dispositivo con conexión. Guardia, citaciones, actos del servicio y movimientos de material, en una sola plataforma.</p>
              <div className="ops-points">
                <div className="ops-point"><span className="ops-num">01</span><p className="ops-pt"><strong>Avisos configurables</strong> · La plataforma muestra notificaciones y envía correos en los eventos habilitados.</p></div>
                <div className="ops-point"><span className="ops-num">02</span><p className="ops-pt"><strong>Historial operativo</strong> · Los módulos registran acciones relevantes para facilitar el seguimiento interno.</p></div>
                <div className="ops-point"><span className="ops-num">03</span><p className="ops-pt"><strong>Acceso web</strong> · Usa la plataforma desde computador o teléfono con conexión a internet.</p></div>
                <div className="ops-point"><span className="ops-num">04</span><p className="ops-pt"><strong>Espacio por compañía</strong> · Cada cuenta accede a la compañía con la que está vinculada.</p></div>
              </div>
            </Rev>
          </div>
        </div>
      </section>

      <div className="tape" />

      {/* ===================== CTA ===================== */}
      <section className="cta" id="sumate">
        <div className="wrap">
          <Rev>
            <div className="cta-mark">
              <svg width="40" height="40" viewBox="0 0 64 64" fill="none" stroke="#fff" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 40a20 20 0 0 1 40 0Z" fill="rgba(255,255,255,0.14)" />
                <path d="M8 40h48v3a5 5 0 0 1-5 5H13a5 5 0 0 1-5-5Z" fill="rgba(255,255,255,0.2)" />
                <path d="M28 26h8l-1.5 9h-5Z" fill="#fff" stroke="none" />
              </svg>
            </div>
            <h2>
              <span className="hl">SUMÁ TU COMPAÑÍA</span>
              <span className="hl"><em>AL CUARTEL DIGITAL.</em></span>
            </h2>
            <p>Gratuito para las compañías de Bomberos de Chile. Registro en línea, acceso por cuenta y soporte en español.</p>
            <div className="cta-actions">
              <Link className="btn btn-primary btn-lg" to="/registro/compania">
                Registrar mi compañía
                <ArrowIcon />
              </Link>
              <button className="btn btn-ghost btn-lg" onClick={() => scrollTo('modulos')}>Ver módulos disponibles</button>
            </div>
          </Rev>
        </div>
      </section>

      {/* ===================== FOOTER ===================== */}
      <footer className="foot">
        <div className="wrap">
          <div className="foot-grid">
            <div>
              <div className="brand">
                <div className="brand-mark"><BrandMark /></div>
                <div>
                  <div className="brand-name">CUARTEL <span>DIGITAL</span></div>
                  <div className="brand-tag">CL · Bomberos de Chile</div>
                </div>
              </div>
              <p className="foot-about">Plataforma de gestión digital para compañías de Bomberos de Chile. Pensada por bomberos, desde el cuartel, para el cuartel. No afiliada oficialmente a la Junta Nacional de Bomberos de Chile. Proyecto particular desarrollado por Benjamín Heresmann Rodríguez — Bombero 2ª Cía. CBVM.</p>
              <a className="foot-wa" href="https://wa.me/56936165140" target="_blank" rel="noopener noreferrer">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
                  <path d="M12.04 2c-5.5 0-9.96 4.46-9.96 9.96 0 1.76.46 3.48 1.34 5L2 22l5.18-1.36a9.9 9.9 0 0 0 4.86 1.24h.01c5.5 0 9.96-4.46 9.96-9.96 0-2.66-1.04-5.16-2.92-7.04A9.88 9.88 0 0 0 12.04 2Zm0 18.2h-.01a8.2 8.2 0 0 1-4.18-1.15l-.3-.18-3.1.81.83-3.02-.2-.31a8.2 8.2 0 0 1-1.26-4.39c0-4.54 3.7-8.24 8.25-8.24a8.2 8.2 0 0 1 8.23 8.25c0 4.54-3.7 8.23-8.25 8.23Zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.12-.16.25-.64.81-.79.97-.14.17-.29.19-.54.06-.25-.12-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.01-.38.11-.5.11-.11.25-.29.37-.43.13-.14.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.4-.42-.56-.43-.14-.01-.31-.01-.48-.01-.17 0-.43.06-.66.31-.23.25-.86.85-.86 2.07 0 1.22.89 2.4 1.01 2.56.12.17 1.75 2.67 4.25 3.75.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.68-1.18.21-.58.21-1.08.14-1.18-.06-.11-.22-.17-.47-.29Z" />
                </svg>
                <span><strong>Benjamín Heresmann</strong> · +56 9 3616 5140</span>
              </a>
            </div>
            <div>
              <h5>Plataforma</h5>
              <ul>
                <li><a onClick={() => scrollTo('modulos')}>Módulos</a></li>
                <li><a onClick={() => scrollTo('operaciones')}>Operaciones</a></li>
                <li><a onClick={() => scrollTo('demo')}>Ver la app</a></li>
                <li><a onClick={() => scrollTo('sumate')}>Registrar compañía</a></li>
              </ul>
            </div>
          </div>
          <div className="foot-bot">
            <div>© 2026 CUARTEL DIGITAL · HECHO EN CHILE</div>
            <div>v3.0 · BOMBEROS</div>
          </div>
        </div>
      </footer>
    </div>
  )
}
